import { datadogLogs } from '@datadog/browser-logs';
import { useCallback } from 'react';

import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import { useAppDispatch } from 'src/store';
import { IProof } from 'src/ts/interfaces';
import { sendReportThunk } from '../services';

export const useSendReport = () => {
  const dispatch = useAppDispatch();
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();

  const onSendReport = useCallback(
    async (entryId?: string, proofs?: IProof[]): Promise<boolean> => {
      if (!entryId) {
        showSnackbar(
          VariantType.error,
          'Is missing the Entry Id to send the report',
          SnackTypes.none
        );

        return false;
      }

      try {
        const result = await dispatch(sendReportThunk({ entryId, proofs })).unwrap();
        const isPartial = result?.data?.partial_completion === true;

        showSnackbar(
          VariantType.success,
          isPartial
            ? 'Review submitted — offer letter awaiting employer verification'
            : 'The report has been sent',
          SnackTypes.none
        );

        return true;
      } catch (error: any) {
        // ITL-2816: sendReportThunk rejects with the backend body via
        // rejectWithValue — shape: { error: { code }, pending_proof_ids }.
        const code = error?.error?.code;

        datadogLogs.logger.error('send_report failed', {
          // ITL-2816 (C9): structured `event` key so the error is searchable by
          // facet, matching every other Datadog log in the app.
          event: 'entries.send_report_failed',
          entry_id: entryId,
          proof_ids: proofs?.map((p) => p.id),
          // ITL-2816 (C9): `error_code` / `pending_proof_ids` are conditional —
          // logging them unconditionally writes `undefined` for non-422 errors and
          // adds index noise. `pending_proof_ids` (when present) are the proofs the
          // SERVER guard flagged as still pending, distinct from client `proof_ids`.
          ...(code ? { error_code: code } : {}),
          ...(error?.pending_proof_ids ? { pending_proof_ids: error.pending_proof_ids } : {}),
          error: error?.error?.message ?? (error instanceof Error ? error.message : String(error))
        });

        showSnackbar(
          VariantType.error,
          code === 'pending_proofs_unresolved'
            ? 'Some documents are still pending review. Resolve all document rulings before sending the report.'
            : 'An error has occurred, please refresh and try again',
          SnackTypes.none
        );

        return false;
      }
    },
    [SnackTypes.none, VariantType.error, VariantType.success, dispatch, showSnackbar]
  );

  return {
    onSendReport
  };
};
